import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useRouter } from 'next/router';
import Head from 'next/head';

import { useCustomTheme } from './theme';
import { ILoggedBusiness } from '../interfaces/i-logged-business';
import { IFCWithChildren } from '../interfaces/i-fc-with-children';
import {
  getBusinessPrimaryColor,
  VMCORE_BRAND,
} from '../constants/vmcore-brand';

interface IBusinessContextData {
  business: ILoggedBusiness | null;
  setBusiness: Dispatch<SetStateAction<ILoggedBusiness | null>>;
}

const BusinessContext = createContext({} as IBusinessContextData);

const BusinessProvider: IFCWithChildren = ({ children }) => {
  const { pathname } = useRouter();
  const { changeBusinessPrimaryColor } = useCustomTheme();
  const [business, setBusiness] = useState<ILoggedBusiness | null>(null);
  const isPlatform = pathname === '/admin' || pathname.startsWith('/admin/');

  useEffect(() => {
    changeBusinessPrimaryColor(
      isPlatform ? null : getBusinessPrimaryColor(business)
    );
  }, [business, changeBusinessPrimaryColor, isPlatform]);

  const contextData = useMemo(
    () => ({
      business,
      setBusiness,
    }),
    [business]
  );

  return (
    <BusinessContext.Provider value={contextData}>
      {(isPlatform || business?.slug === 'vmcore') && (
        <Head>
          <meta name="theme-color" content={VMCORE_BRAND.navy} />
          <link rel="icon" type="image/svg+xml" href={VMCORE_BRAND.icon} />
        </Head>
      )}
      {children}
    </BusinessContext.Provider>
  );
};

const useBusiness = (): IBusinessContextData => useContext(BusinessContext);

export { BusinessProvider, useBusiness };
